// CANONICAL — this is the active component. src/features/leases/LeasesView.jsx is legacy/dead.
// src/v2/leases/LeasesPage.jsx
//
// Portfolio is the canonical /leases default; Timeline is /leases/timeline.
// Both reuse their existing content and share lease-scoped shell navigation.
//
// Data flow:
//   1. Fetch portfolio leases  ← getPortfolioLeases()
//   2. Fetch critical dates    ← getCriticalDates()
//   3. Enrich with deriveLeaseActions() → bucket + days + action type
//   4. Apply search client-side
//   5. Render grouped timeline (timeline) or list (list)

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useCountUp } from "../hooks/useCountUp.js";
import { ChevronDown } from "lucide-react";
import { T } from "../../app/tokens.js";
import { getPortfolioLeases, getCriticalDates, getUnspentAllowanceRows } from "../../data/leasesPortfolioClient.js";
import { deriveLeaseActions, OUTLOOK_DESCRIPTOR_ORDER } from "../../features/leases/deriveLeaseActions.js";
import { statusFor, isActiveOrCommitted, isActiveOccupancy } from "../../features/leases/pages/components/LeasePortfolioFilters.jsx";
import { timelineLaneFor } from "./timelineLanes.js";
import { LEASE_TYPES, LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { log } from "../../utils/logger.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { LeasesRoute } from "./LeasesRoute.jsx";
import { LeasesListTable } from "../../features/leases/pages/components/LeasesListTable.jsx";
import {
  LeasesTimelineView,
  TIMELINE_GROUPS,
  applyTimelineLens,
  getTimelineDateCoverage,
} from "./LeasesTimelineView.jsx";
import { TIMELINE_BAR_PHASES } from "./timelineBarPhases.js";
import { AlertChip } from "../components/AlertChip.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../../features/plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import { SearchInput } from "../components/SearchInput.jsx";
import { FilterHiddenSearchHint } from "../components/FilterHiddenSearchHint.jsx";
import { activeFilterGroups, restrictedFilterGroups, relaxFilterGroups, deriveFilterHiddenSearch } from "../../utils/filterHiddenSearch.js";
import { CHECKED_FILTER_VERSION, allOptionValues, normalizeCheckedSelections, checkedPredicateSelections, withFacetCounts } from "../../utils/checkedSelections.js";
import { SortControl } from "../components/SortControl.jsx";
import { ConsolidatedFilter } from "../components/ConsolidatedFilter.jsx";
import { InlineFilterToolbar, InlineFilterChips, InlineFilterClear, facetChips } from "../components/InlineFilterToolbar.jsx";
import { ViewControls, CompactSwitch } from "../components/ViewControls.jsx";
import { ToolbarAddButton } from "../components/ToolbarAddButton.jsx";
import { useNavigate } from "react-router-dom";
import { ViewKey } from "../components/ViewKey.jsx";
import { LeaseRecordDrawer } from "./LeaseRecordDrawer.jsx";
import { buildLeaseUrl, buildLeaseFullUrl, buildOfficeDrawerUrl, leaseRecordFromPath } from "../../services/buildUrl.js";
import { readRecordReturn, recordSwapState } from "../../services/recordReturn.js";
import { CreateLeaseModal } from "../../features/leases/CreateLeaseModal.jsx";
import { useUiPrefs } from "../hooks/useUiPrefs.js";
import { UI_PREFS_LEASES_V1 } from "../../services/portfolioStatePersistence.js";
// computeLeaseRisk import removed (L-34): risk_score sort now uses
// OUTLOOK_DESCRIPTOR_ORDER composite key instead of the continuous score.
import { resolveTimelineEndAnchor, parseDateOnly } from "../../features/leases/leaseTabConfig.js";
import { STAGE_ORDER } from "../../utils/rankSites.js";
import { STATUS_LABELS } from "../../features/leases/pages/components/LeasesListTable.jsx";
import { matchLeaseIdentity } from "../../features/leases/leaseIdentitySearch.js";
import { rankIdentityMatches } from "../../utils/curatedSearch.js";
import {
  LEASE_DECISION_WINDOW_MONTHS,
  LEASE_DECISION_WINDOW_SCOPE,
} from "../../shared/leaseDecisionWindow.js";
import { useFxConversionBasis, formatFxBasisSummary } from "../../data/useFxConversionBasis.js";
import { DEFAULT_LEASE_STATUS_STATUSES, deriveAnnualRentKpi } from "../../features/leases/annualRentKpi.js";
import { currentLeaseFootprint, displayedLeasePsf } from "../../../shared/leaseAreaBasis.js";
export { DEFAULT_LEASE_STATUS_STATUSES } from "../../features/leases/annualRentKpi.js";
// ─── Constants ────────────────────────────────────────────────────────────────

const TODAY = new Date();

// Data-accent palette for KPI tiles — mirrors the Locations v2 strip order.
const KPI_ACCENTS = [
  "var(--v2-accent)",
  "var(--v2-green)",
  "var(--v2-gold)",
  "var(--v2-teal)",
  "var(--v2-purple)",
  "var(--v2-red)",
];

// One ordered default contract for the page and standalone Timeline.
export const DEFAULT_EXPANDED_GROUPS = Object.fromEntries(
  TIMELINE_GROUPS.map((group) => [group.bucket, group.expandedByDefault]),
);

// ─── Pure helpers ─────────────────────────────────────────────────────────────

// SEARCH-SCOPE-STANDARDIZE — Apply search filter across the IDENTITY baseline:
// city (market), locationAlias, locationId, region, landlord, owner.
// All comparisons are diacritic-insensitive, case-insensitive substring matches
// using the shared text-search matcher.
export function applySearch(leases, term) {
  if (!term || !term.trim()) return leases;
  return leases.filter((l) => matchLeaseIdentity(l, term).eligible);
}

// Shared List and Timeline Status baseline. Explicit selections override this;
// Draft and Expired are excluded until a user selects them.
// Keep the historical exports as aliases for callers/tests while ensuring all
// view behavior derives from the same canonical baseline.
export const DEFAULT_ACTIVE_STATUSES = Object.freeze(["in_use", "vacant_held"]);
export const DEFAULT_LIST_STATUSES = DEFAULT_LEASE_STATUS_STATUSES;
export const DEFAULT_TIMELINE_STATUSES = DEFAULT_LEASE_STATUS_STATUSES;

// Presentation copy is separate from the shared runway phase definitions.
const TIMELINE_KEY_DESCRIPTIONS = {
  decide: "within 90 days of the notice or expiry date",
  negotiate: "3 to 12 months before the notice or expiry date",
  monitor: "more than 12 months before the notice or expiry date",
};
const TIMELINE_KEY_GROUPS = [
  {
    label: "Runway",
    items: TIMELINE_BAR_PHASES.map((phase) => ({
      label: phase.label,
      description: TIMELINE_KEY_DESCRIPTIONS[phase.key],
      swatch: { kind: "bar", color: phase.color },
    })),
  },
  {
    label: "Markers",
    items: [
      {
        label: "Notice date",
        description: 'after it passes while expiry is ahead, an amber "Notice open" tag replaces it and the track counts down to expiry',
        swatch: { kind: "ring", color: T.mid },
      },
      { label: "Lease expiry", swatch: { kind: "tick", color: T.dark } },
    ],
  },
];

function TimelineViewKey({ coverage }) {
  return (
    <ViewKey
      testId="leases-timeline-key"
      title="How to read the timeline"
      groups={TIMELINE_KEY_GROUPS}
      footer={
        <span data-testid="timeline-data-coverage">
          <strong style={{ color: T.dark }}>Data coverage:</strong>
          {" "}Expiry or projected end on {coverage.expiryDateCount}/{coverage.leaseCount} leases.
          {" "}Current-term notice, break, or renewal dates on {coverage.currentTermActionDateCount}/{coverage.leaseCount}.
          {" "}Earliest termination recorded on {coverage.earliestTerminationDateCount}/{coverage.leaseCount}.
          {" "}Rows without a verified decision date use expiry.
        </span>
      }
    />
  );
}

// Apply ConsolidatedFilter selections. AND across dimensions, OR within.
// Status uses statusFor() for consistency with the legacy LeasesView filter logic.
export function applyConsolidatedFilters(leases, selections, checked = false, countGroup = null) {
  if (checked) {
    // Timeline retains its pre-existing empty-group eligibility. Only List
    // opts into match-none, while fully checked groups bypass classification.
    selections = checkedPredicateSelections(LEASES_FILTER_DIMENSIONS, selections, checked === "legacy-empty", countGroup);
    if (!selections) return [];
  }
  return leases.filter((l) => {
    if (selections.status?.length > 0) {
      const bucket = statusFor(l);
      if (!selections.status.includes(bucket)) return false;
    }
    if (selections.leaseType?.length > 0) {
      if (!selections.leaseType.includes(l.leaseType)) return false;
    }
    return true;
  });
}

// A blank amount means missing rent only when the persisted basis says rent is
// fixed. Other explicit bases describe why there is no numeric rent to report.
export function isLeaseRentMissing(lease) {
  const listRentUsd = lease.listDisplayRentUsd ?? lease.annualRentUsd;
  return lease.rentBasis === "fixed"
    && listRentUsd == null
    && (lease.status === "active" || lease.status === "holdover");
}

// Sort the flat lease array by the chosen sort key.
export function sortLeases(leases, sortKey) {
  const copy = [...leases];
  switch (sortKey) {
    case "due-soonest":
      return copy.sort((a, b) => {
        if (a.days_to_next_action === null) return 1;
        if (b.days_to_next_action === null) return -1;
        return a.days_to_next_action - b.days_to_next_action;
      });
    case "due-latest":
      return copy.sort((a, b) => {
        if (a.days_to_next_action === null) return -1;
        if (b.days_to_next_action === null) return 1;
        return b.days_to_next_action - a.days_to_next_action;
      });
    case "exposure-high":
      return copy.sort((a, b) => (b.action_value ?? 0) - (a.action_value ?? 0));
    case "exposure-low":
      return copy.sort((a, b) => (a.action_value ?? 0) - (b.action_value ?? 0));
    default:
      return copy;
  }
}

// ── Filter dimensions config (LEASES-FILTER-BUTTON-FIX-S1) ────────────────────
// Mirrors OfficesPage's FILTER_DIMENSIONS shape for ConsolidatedFilter, scoped
// to the fields applyConsolidatedFilters() actually understands.
const LEASES_FILTER_DIMENSIONS = [
  {
    id: "status",
    label: "Status",
    options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
  },
  {
    id: "leaseType",
    label: "Lease type",
    options: LEASE_TYPES.map((t) => ({ value: t, label: LEASE_DISPLAY[t] })),
  },
];

function migrateLeasePrefs(state) {
  return {
    ...state,
    filterVersion: CHECKED_FILTER_VERSION,
    selections: normalizeCheckedSelections(LEASES_FILTER_DIMENSIONS, state.selections, {}, {
      legacy: state.filterVersion !== CHECKED_FILTER_VERSION,
      emptyMeansNone: ["status"],
      // An absent status has a special bucket-arrival meaning; don't persist
      // an explicit baseline that would accidentally override that eligibility.
      preserveMissing: ["status"],
    }),
  };
}

// ─── Shared toolbar SortControl (LEASES-SORT-COLUMNS-S1) ───────────────────────
//
// Fixed, ordered 9-field list matching the visible column headers:
// Office · Hub · Outlook · Status · Expire Date · Arrangement · Term Type · Sq Ft · Annual Rent · $/sf.
// Numeric fields default to desc-first (highest value first); text fields
// default to asc-first (A→Z). Hub is a custom ordering (hub leases first via
// getLeaseSortValue returning 0/1) and intentionally omits `numeric: true` so
// SortControl's first-click default is ascending.
export const LEASES_SORT_FIELDS = [
  { value: "location",   label: "Office"       },
  { value: "hub",        label: "Hub"           },
  { value: "risk_score", label: "Outlook",   numeric: true },
  { value: "status",     label: "Status"        },
  { value: "end_date",   label: "Expire Date"   },
  { value: "type",       label: "Arrangement"   },
  { value: "term_type",  label: "Term Type"     },
  { value: "sqft",       label: "Sq Ft",     numeric: true },
  { value: "total_cost", label: "Annual Rent", numeric: true },
  { value: "psf",        label: "$/sf",      numeric: true },
];

// Effective default; explicit stored and URL choices retain their precedence.
export const DEFAULT_LEASES_SORT_STATE = {
  sortKey:        "end_date",
  sortDir:        "asc",
  sortKey2:       null,
  sortDir2:       "asc",
  userHasSetSort: false,
};

// Sentinel returned by getLeaseSortValue when a lease has no data for the
// chosen key. sortLeaseRows always moves such rows to the bottom, independent
// of sort direction — mirrors the BLANK convention in OfficesPage.
const LEASE_SORT_BLANK = Symbol("blank");

export function getLeaseSortValue(lease, sortKey) {
  switch (sortKey) {
    case "term_type": return lease.termType?.toLowerCase() || LEASE_SORT_BLANK;
    case "end_date": {
      const anchor = resolveTimelineEndAnchor(lease);
      if (!anchor.date) return LEASE_SORT_BLANK;
      const d = parseDateOnly(anchor.date);
      return d ? d.getTime() : LEASE_SORT_BLANK;
    }
    case "location": {
      const v = (lease.city || lease.locationId || "").toLowerCase();
      return v || LEASE_SORT_BLANK;
    }
    case "risk_score": {
      // L-34: per-descriptor ordinal (primary) + expire-date ms (secondary).
      // Mirrors the composite key used in LeasesListTable.jsx so both sort
      // paths produce identical ordering. OUTLOOK_DESCRIPTOR_ORDER gives each
      // amber bucket its own rank; unknown / null buckets fall to ordinal 9.
      const descriptorOrdinal = OUTLOOK_DESCRIPTOR_ORDER[lease._healthBucket] ?? 9;
      const anchor = resolveTimelineEndAnchor(lease);
      const d = anchor.date ? parseDateOnly(anchor.date) : null;
      const endDateMs = d ? d.getTime() : Number.MAX_SAFE_INTEGER;
      return descriptorOrdinal * 1e13 + endDateMs;
    }
    case "total_cost":
      // Sort by list-specific display rent, not current-only KPI rent.
      {
        const listRentUsd = lease.listDisplayRentUsd !== undefined ? lease.listDisplayRentUsd : lease.annualRentUsd;
        return listRentUsd != null ? Number(listRentUsd) : LEASE_SORT_BLANK;
      }
    case "psf": {
      // Match list display rent and keep mixed-currency comparisons in USD.
      return displayedLeasePsf(lease) ?? LEASE_SORT_BLANK;
    }
    case "type": {
      const raw = lease.leaseType || lease.type || "";
      const label = LEASE_DISPLAY[raw] || raw;
      return label ? label.toLowerCase() : LEASE_SORT_BLANK;
    }
    case "hub":
      return (lease.isHub || lease.is_hub) ? 0 : 1;
    case "stage": {
      const order = STAGE_ORDER[lease.locationStage];
      return order != null ? Number(order) : LEASE_SORT_BLANK;
    }
    case "status": {
      const bucket = statusFor(lease);
      const label = STATUS_LABELS[bucket] || bucket;
      return label ? label.toLowerCase() : LEASE_SORT_BLANK;
    }
    case "expire_type": {
      const anchor = resolveTimelineEndAnchor(lease);
      if (anchor.mode === "contractual") return "contractual";
      if (anchor.mode === "projected_expiration" || anchor.mode === "projected_notice") return "projected";
      return LEASE_SORT_BLANK;
    }
    case "renewal_option": {
      const renewalCount = Number(lease.renewalCount) || 0;
      return renewalCount > 0 ? 0 : 1;
    }
    case "sqft":
      return currentLeaseFootprint(lease).grossSf ?? LEASE_SORT_BLANK;
    default:
      return LEASE_SORT_BLANK;
  }
}

export function sortLeaseRows(rows, sortKey, sortDir, sortKey2 = null, sortDir2 = "asc") {
  const dir  = sortDir  === "desc" ? -1 : 1;
  const dir2 = sortDir2 === "desc" ? -1 : 1;
  const today = new Date();
  const todayMs = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

  // Partition: rows with a real value vs. rows that have no data (BLANK).
  // Blank rows are always appended at the end regardless of sort direction.
  const real  = [];
  const blank = [];
  for (const row of rows) {
    if (getLeaseSortValue(row, sortKey) === LEASE_SORT_BLANK) blank.push(row);
    else real.push(row);
  }

  real.sort((a, b) => {
    const va = getLeaseSortValue(a, sortKey);
    const vb = getLeaseSortValue(b, sortKey);
    // Keep canonical term/projected anchors intact; only group their display
    // ordering. Current/upcoming precede past dates in either direction.
    if (sortKey === "end_date") {
      const pastA = va < todayMs || statusFor(a) === "expired";
      const pastB = vb < todayMs || statusFor(b) === "expired";
      if (pastA !== pastB) return pastA ? 1 : -1;
    }
    // Stable tiebreaker shared by all paths: A–Z by location, case-insensitive.
    const nameA = (a.city || a.locationId || "").toLowerCase();
    const nameB = (b.city || b.locationId || "").toLowerCase();

    function tiebreak() {
      if (sortKey2) {
        const va2 = getLeaseSortValue(a, sortKey2);
        const vb2 = getLeaseSortValue(b, sortKey2);
        if (va2 === LEASE_SORT_BLANK && vb2 !== LEASE_SORT_BLANK) return 1;
        if (vb2 === LEASE_SORT_BLANK && va2 !== LEASE_SORT_BLANK) return -1;
        if (va2 !== LEASE_SORT_BLANK && vb2 !== LEASE_SORT_BLANK) {
          const secondary = typeof va2 === "string" && typeof vb2 === "string"
            ? va2.localeCompare(vb2) * dir2
            : (va2 - vb2) * dir2;
          if (secondary !== 0) return secondary;
        }
      }
      return nameA.localeCompare(nameB) || String(a.id).localeCompare(String(b.id));
    }

    if (typeof va === "string" && typeof vb === "string") {
      const primary = va.localeCompare(vb) * dir;
      if (primary !== 0) return primary;
      return tiebreak();
    }
    const primary = (va - vb) * dir;
    if (primary !== 0) return primary;
    return tiebreak();
  });

  // Missing primary values still honor Then by and deterministic identity.
  blank.sort((a, b) => {
    if (sortKey2) {
      const va = getLeaseSortValue(a, sortKey2);
      const vb = getLeaseSortValue(b, sortKey2);
      if (va !== vb) {
        if (va === LEASE_SORT_BLANK) return 1;
        if (vb === LEASE_SORT_BLANK) return -1;
        const secondary = typeof va === "string" ? va.localeCompare(vb) * dir2 : (va - vb) * dir2;
        if (secondary) return secondary;
      }
    }
    return String(a.city || a.locationId || "").localeCompare(String(b.city || b.locationId || ""), undefined, { sensitivity: "base" })
      || String(a.id).localeCompare(String(b.id));
  });
  return [...real, ...blank];
}

// ─── KPI date formatter ───────────────────────────────────────────────────────

const _KPI_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtExpiryDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00Z");
  if (isNaN(d.getTime())) return "—";
  return `${_KPI_MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

// Format avg term months: < 12 → "X mo", ≥ 12 → "X.X yr" (1 decimal).
function fmtAvgTerm(months) {
  if (months == null) return "—";
  const unitStyle = { marginLeft: "2px" };
  if (months < 12) {
    return (
      <>
        {Math.round(months)}
        <span style={unitStyle}>mo</span>
      </>
    );
  }
  const yrs = months / 12;
  return (
    <>
      {yrs.toFixed(1)}
      <span style={unitStyle}>yr</span>
    </>
  );
}

function fmtWale(years) {
  if (years == null) return "—";
  return (
    <>
      {years.toFixed(1)}
      <span style={{ marginLeft: "2px" }}>yr</span>
    </>
  );
}

// Derive the KPI tile values from the filtered derived-lease set.
//
// LEASE-COST-FX-FIX-S1: "Total Annual Rent" (Metric A, summed) and "Total
// Exposure" (remaining-term lifetime commitment) are intentionally DIFFERENT
// figures — both FX-converted to USD server-side by the /api/leases route
// (server/lib/leaseCostSourcing.js). Previously both read the same
// action_value (raw native totalCost), so they were always identical.
// Lease-set scope is unchanged from before this fix (whatever the caller
// passes in, e.g. after ConsolidatedFilter narrowing) — this fix only swaps
// the underlying value fields, not which leases are included.
export function deriveKpiTiles(derivedLeases, allowanceRows = [], today = new Date()) {
  const msPerMonth = 30.4375 * 24 * 60 * 60 * 1000;
  const activeLeases = derivedLeases.filter(
    (lease) => isActiveOccupancy(lease, today),
  );

  // 1. Lease count — Active (In-Use/Vacant-Held) plus Committed, never Draft/Expired.
  const leaseCount = derivedLeases.filter((lease) => isActiveOrCommitted(lease, today)).length;

  // Count recorded leased area, not the Offices operational footprint. Outbound
  // children never add area, even when their head is outside the current filter.
  let totalLeasedSqft = 0;
  let areaEligibleCount = 0;
  let missingAreaCount = 0;
  let excludedOutboundCount = 0;
  for (const lease of derivedLeases) {
    if (!isActiveOrCommitted(lease, today)) continue;
    if (lease.leaseType === "sublease_outbound") {
      excludedOutboundCount++;
      continue;
    }
    areaEligibleCount++;
    const rawArea = currentLeaseFootprint(lease, today).grossSf;
    // Validate before conversion: Number(null), Number("") and Number(false)
    // would otherwise manufacture recorded zeros. A real numeric zero is valid.
    const area = typeof rawArea === "number"
      || (typeof rawArea === "string" && rawArea.trim() !== "")
      ? Number(rawArea)
      : NaN;
    if (!Number.isFinite(area) || area < 0) {
      missingAreaCount++;
      continue;
    }
    totalLeasedSqft += area;
  }

  // 2. Total Annual Rent — sum of Metric A (current-year rent, FX→USD) across the
  // filtered lease set (unchanged scope from before this fix — see below).
  const annualRentKpi = deriveAnnualRentKpi(derivedLeases);

  // 3. Avg term remaining — average months to soonestPossibleExpiration (or
  // endDate fallback), excluding past dates and the sentinel 9999-12-31.
  const termDates = [];
  for (const l of activeLeases) {
    const dateStr = l.soonestPossibleExpiration || l.endDate;
    if (!dateStr || dateStr === "9999-12-31") continue;
    const d = new Date(dateStr + "T00:00:00Z");
    if (isNaN(d.getTime())) continue;
    if (d <= today) continue;
    termDates.push(d);
  }
  const avgTermMonths = termDates.length > 0
    ? Math.round(termDates.reduce((s, d) => s + (d - today) / msPerMonth, 0) / termDates.length)
    : null;

  // WALE uses the same resolved end-date anchor shown in the Leases list and
  // remaining lifetime USD exposure (not annual or historical list rent).
  // Leases with incomplete inputs and leases without a usable date are excluded
  // and counted; valid rows still produce a partial weighted result.
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  let waleWeightedYears = 0;
  let waleExposure = 0;
  let waleIncludedCount = 0;
  let waleUndatedCount = 0;
  let waleIncompleteCount = 0;
  for (const l of activeLeases) {
    const { date: endDateValue } = resolveTimelineEndAnchor(l);
    let rawEndDate;
    if (endDateValue instanceof Date) {
      if (Number.isNaN(endDateValue.getTime())) {
        waleIncompleteCount++;
        continue;
      }
      rawEndDate = endDateValue.toISOString().slice(0, 10);
    } else {
      rawEndDate = String(endDateValue ?? "").trim().slice(0, 10);
    }
    if (!rawEndDate || rawEndDate === "9999-12-31") {
      waleUndatedCount++;
      continue;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(rawEndDate)) {
      waleIncompleteCount++;
      continue;
    }
    const endDate = new Date(`${rawEndDate}T00:00:00Z`);
    if (Number.isNaN(endDate.getTime()) || endDate.toISOString().slice(0, 10) !== rawEndDate) {
      waleIncompleteCount++;
      continue;
    }

    if (l.remainingExposureUsd == null || l.remainingExposureUsd === "") {
      waleIncompleteCount++;
      continue;
    }
    const exposure = Number(l.remainingExposureUsd);
    if (!Number.isFinite(exposure) || exposure < 0) {
      waleIncompleteCount++;
      continue;
    }

    waleIncludedCount++;
    waleExposure += exposure;
    waleWeightedYears += exposure * Math.max(0, endDate.getTime() - todayUtc) / (365.25 * 24 * 60 * 60 * 1000);
  }
  const waleYears = waleExposure > 0
    ? waleWeightedYears / waleExposure
    : null;

  // Separate urgency signal: the board's Decide now action bucket remains 0–90 days.
  const decideNowCount = derivedLeases.filter(
    (l) => l.action_window_bucket === "decide-now",
  ).length;

  // 4b. Vacated · to dispose — posture rows in the combined Reduce exposure band.
  const subleaseCount    = derivedLeases.filter((l) => l.action_window_bucket === "reduce-exposure" && l.posture === "sublease").length;
  const windingDownCount = derivedLeases.filter((l) => l.action_window_bucket === "reduce-exposure" && l.posture === "winding-down").length;
  const vacatedCount     = subleaseCount + windingDownCount;

  // 5. Next Expiry — soonest future soonestPossibleExpiration (or endDate fallback).
  // Kept for status-message use; no longer rendered as a KPI tile.
  let nextExpiryLease = null;
  let nextExpiryDate  = null;
  for (const l of derivedLeases) {
    const dateStr = l.soonestPossibleExpiration || l.endDate;
    if (!dateStr || dateStr === "9999-12-31") continue;
    const d = new Date(dateStr + "T00:00:00Z");
    if (isNaN(d.getTime()) || d < today) continue;
    if (!nextExpiryDate || d < nextExpiryDate) {
      nextExpiryDate  = d;
      nextExpiryLease = l;
    }
  }
  let nextExpiry = null;
  if (nextExpiryLease && nextExpiryDate) {
    const monthsAway = Math.round((nextExpiryDate - today) / msPerMonth);
    nextExpiry = {
      dateStr:    nextExpiryLease.soonestPossibleExpiration || nextExpiryLease.endDate,
      city:       nextExpiryLease.city || nextExpiryLease.locationId || "",
      monthsAway,
    };
  }

  // 6. Unused TI Allowance + near-expiry count (≤90 days, the isTiNearExpiry
  // predicate equivalent from the deleted LeasesView).
  const NEAR_EXPIRY_DAYS = 90;
  const leaseIdSet = new Set(derivedLeases.map((l) => l.id));
  let unusedTi = 0;
  let nearExpiryTiCount = 0;
  for (const row of (allowanceRows || [])) {
    if (row.type !== "ti_allowance") continue;
    if (!leaseIdSet.has(row.leaseId)) continue;
    const unused = Math.max(0, Number(row.totalAmount || 0) - Number(row.drawnToDate || 0));
    unusedTi += unused;
    if (unused > 0 && row.daysUntilExpiry != null && row.daysUntilExpiry <= NEAR_EXPIRY_DAYS) {
      nearExpiryTiCount++;
    }
  }

  // 7. Total Exposure — remaining-term lifetime commitment (FX→USD), a
  // different figure from Total Annual Rent by design (see banner above).
  // Kept for potential future use; not currently rendered as a tile.
  const totalExposure = derivedLeases.reduce((s, l) => s + (l.remainingExposureUsd ?? 0), 0);

  // 8. Expiring < 12 mo — count of leases whose resolved expiry date falls in
  // [today, today + 12 months). Uses the same date-resolution order as the
  // Expire Date column (resolveTimelineEndAnchor): endDate-first for
  // non-sentinel leases, SPE for rolling leases. Excludes past-due dates so
  // holdover / expired leases whose contractual endDate is already behind today
  // are never counted, even if their SPE is in the forward window.
  const MS_12_MONTHS = 365 * 24 * 60 * 60 * 1000;
  let expiringWithin12moCount = 0;
  for (const l of derivedLeases) {
    const { date: dateStr } = resolveTimelineEndAnchor(l);
    if (!dateStr) continue;
    const d = new Date(dateStr + "T00:00:00Z");
    if (isNaN(d.getTime())) continue;
    const msUntil = d - today;
    if (msUntil > 0 && msUntil <= MS_12_MONTHS) expiringWithin12moCount++;
  }

  return { leaseCount, totalLeasedSqft, areaEligibleCount, missingAreaCount, excludedOutboundCount, ...annualRentKpi, avgTermMonths, waleYears, waleIncludedCount, waleTotalCount: activeLeases.length, waleUndatedCount, waleIncompleteCount, decideNowCount, nextExpiry, unusedTi, nearExpiryTiCount, totalExposure, vacatedCount, subleaseCount, windingDownCount, expiringWithin12moCount };
}

// ─── URL param encode / decode (LEASES-VIEW-IN-URL + LEASES-URL-PARAMS) ──────
//
// Encodes view, filters, search, sort, and density so any Leases page state
// can be shared as a URL. Unknown values are silently dropped.

// Filter dimensions that round-trip through the URL.
const LEASES_URL_FILTER_DIMS = ["status", "leaseType"];

// Sort keys whose natural first-click direction is descending (highest value first).
const LEASES_NUMERIC_SORT_KEYS = new Set(["risk_score", "total_cost", "psf", "sqft"]);

/**
 * Decode window.location.search into a URL param bag for LeasesPage.
 * Returns null when no recognised params are found.
 * Never throws.
 *
 * Recognised params:
 *   view        — "list" | "timeline"
 *   status      — comma-separated status values
 *   leaseType   — comma-separated leaseType values
 *   sort        — sort key
 *   sortDir     — "asc" | "desc"
 *   q           — search query
 *   density     — "compact" (comfortable is the default, omitted)
 */
export function decodeLeasesParams(search, routeView = null) {
  try {
    const p = new URLSearchParams(search);
    const bag = {};
    let hasAny = false;
    if (p.get("filters") === "checked") {
      bag.checkedFilters = true;
      hasAny = true;
    }

    const view = p.get("collection") || p.get("view");
    if (view === "list" || view === "timeline") {
      bag.view = view;
      hasAny = true;
    }

    for (const dim of LEASES_URL_FILTER_DIMS) {
      const raw = p.get(dim);
      if (raw !== null) {
        bag[dim] = raw === "" && dim !== "status" && p.get("filters") !== "checked"
          ? allOptionValues(LEASES_FILTER_DIMENSIONS.find(({ id }) => id === dim))
          : raw.split(",").filter(Boolean);
        hasAny = true;
      } else if (p.get("filters") === "checked") {
        // Missing Status remains implicit, including after shared-link hydration,
        // so a later Home bucket arrival can still bypass the default gate.
        if (dim !== "status") bag[dim] = allOptionValues(LEASES_FILTER_DIMENSIONS.find(({ id }) => id === dim));
        hasAny = true;
      }
    }

    const sort = p.get("sort");
    if (sort) {
      bag.sort = sort;
      hasAny = true;
    }

    const sortDir = p.get("sortDir");
    if (sortDir === "asc" || sortDir === "desc") {
      bag.sortDir = sortDir;
    }

    const q = p.get("q");
    if (q != null && q !== "") {
      bag.q = q;
      hasAny = true;
    }

    const density = p.get("density");
    if (density === "compact") {
      bag.density = density;
      hasAny = true;
    }

    const peek = p.get("peek");
    if (peek) {
      bag.peek = peek;
      hasAny = true;
    }
    const focus = p.get("focus");
    if ((view === "timeline" || routeView !== null) && focus) {
      bag.focus = focus;
      hasAny = true;
    }

    if (p.get("decisionScope") === LEASE_DECISION_WINDOW_SCOPE) {
      bag.decisionScope = LEASE_DECISION_WINDOW_SCOPE;
      bag.decisionLeaseIds = [...new Set(p.getAll("decisionLeaseId").filter(Boolean))];
      hasAny = true;
    }

    if (p.get("showAll") === "1") {
      bag.showAll = true;
      hasAny = true;
    }

    const bucketsRaw = p.get("buckets");
    if (bucketsRaw) {
      bag.buckets = bucketsRaw.split(",").filter(Boolean);
      hasAny = true;
    }

    return hasAny ? bag : null;
  } catch {
    return null;
  }
}

/**
 * Encode the current page state into a query string (without the leading "?").
 * Returns "" when the state is fully default so URLs stay clean.
 *
 * Encoding rules:
 *   view       — omit "list" (URL default); include "timeline"
 *   filter dims — comma-separated; omit when empty
 *   sort       — included only when userHasSetSort is true
 *   sortDir    — included when sort is included and direction is desc
 *   q          — included when non-empty
 *   density    — included only when "compact"
 *
 * @param {object} state
 * @param {string} [state.view]
 * @param {string} [state.search]
 * @param {object} [state.selections]
 * @param {string} [state.sortKey]
 * @param {string} [state.sortDir]
 * @param {boolean} [state.userHasSetSort]
 * @param {string} [state.density]
 */
function _leasesArrEqSet(a, b) {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((v) => s.has(v));
}

export function encodeLeasesParams({ view, search, selections, filterVersion, sortKey, sortDir, userHasSetSort, density, peekId, focusId, showAll, buckets, decisionScope, decisionLeaseIds } = {}) {
  const p = new URLSearchParams();

  // view: omit when absent or the URL default (list); include for timeline.
  if (view && view !== "list") {
    p.set("view", view);
  }

  for (const dim of LEASES_URL_FILTER_DIMS) {
    const vals = selections?.[dim] ?? [];
    // Omit status when it matches the shared silent default in either view.
    if (dim === "status") {
      if (_leasesArrEqSet(vals, DEFAULT_LEASE_STATUS_STATUSES)) continue;
    }
    if (filterVersion === CHECKED_FILTER_VERSION) {
      const defaultVals = dim === "status" ? DEFAULT_LEASE_STATUS_STATUSES
        : allOptionValues(LEASES_FILTER_DIMENSIONS.find(({ id }) => id === dim));
      if (selections?.[dim] === undefined || _leasesArrEqSet(vals, defaultVals)) continue;
      p.set(dim, vals.join(","));
      p.set("filters", "checked");
    } else if (vals.length > 0) {
      p.set(dim, vals.join(","));
    } else if (dim === "status" && selections?.status !== undefined) {
      p.set(dim, "");
    }
  }

  if (userHasSetSort && sortKey) {
    p.set("sort", sortKey);
    if (sortDir && sortDir !== "asc") {
      p.set("sortDir", sortDir);
    }
  }

  if (search) {
    p.set("q", search);
  }

  if (density === "compact") {
    p.set("density", "compact");
  }

  if (peekId) {
    p.set("peek", peekId);
  }
  if (view === "timeline" && focusId) {
    p.set("focus", focusId);
  }

  if (showAll) {
    p.set("showAll", "1");
  }

  if (buckets?.length > 0) {
    p.set("buckets", buckets.join(","));
  }

  if (decisionScope === LEASE_DECISION_WINDOW_SCOPE) {
    p.set("decisionScope", LEASE_DECISION_WINDOW_SCOPE);
    for (const leaseId of decisionLeaseIds ?? []) {
      p.append("decisionLeaseId", leaseId);
    }
  }

  if (filterVersion === CHECKED_FILTER_VERSION && p.size > 0) p.set("filters", "checked");
  return p.toString();
}



// ─── Main page component ──────────────────────────────────────────────────────

export function LeasesPage(props) {
  return <LeasesRoute>{({ location, replaceDisplayUrl, revision }) => (
    <LeasesPresentation key={revision} {...props} restoreUrlDefaults={revision > 0} routeLocation={location} replaceDisplayUrl={replaceDisplayUrl} />
  )}</LeasesRoute>;
}

function LeasesPresentation({ routeLocation, replaceDisplayUrl, restoreUrlDefaults, onSelectLocation, currentUser, currentRole = "viewer", onOpenImportsQueue, onGoToPlan, onSubtitleChange, onNavListChange = null }) {
  const urlBagRef = useRef(undefined);
  if (urlBagRef.current === undefined) {
    urlBagRef.current = decodeLeasesParams(routeLocation.search, routeLocation.pathname);
  }

  const { basis: fxBasis, liveFetchedAt: fxLiveFetchedAt } = useFxConversionBasis();
  const fxBasisSummary = formatFxBasisSummary(fxBasis, fxLiveFetchedAt);

  // LEASES-VIEW-IN-URL: React Router navigate (write-only; safe in routed components).
  const navigate = useNavigate();

  const [leases,        setLeases]        = useState([]);
  const [criticalDates, setCriticalDates] = useState([]);
  const [allowanceRows, setAllowanceRows] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  // CreateLeaseModal state (L-47)
  const [showCreateLease,  setShowCreateLease]  = useState(false);
  const [leaseRefreshKey,  setLeaseRefreshKey]  = useState(0);
  const canWrite = currentRole === "admin" || currentRole === "editor";
  useEffect(() => {
    if (!canWrite) setShowCreateLease(false);
  }, [canWrite]);

  // ── Peek drawer ─────────────────────────────────────────────────────────────
  // `drawerLease` is null (closed) or a lease list-row object (open).
  // The drawer lease ID is also encoded in the URL as ?peek={leaseId}.
  const drawerLeaseId = leaseRecordFromPath(routeLocation.pathname);
  const drawerTab = new URLSearchParams(routeLocation.search).get("tab") || "overview";
  const drawerLease = useMemo(() => drawerLeaseId ? { id: drawerLeaseId } : null, [drawerLeaseId]);
  const openRecord = useCallback((lease, options = {}) => {
    if (!lease?.id) return;
    const params = new URLSearchParams(routeLocation.search);
    if (params.get("collection") === "timeline" || routeLocation.pathname === "/leases/timeline") params.set("collection", "timeline");
    navigate(buildLeaseUrl(lease.id, options.tab || "overview", params), {
      replace: options.replace || false, state: routeLocation.state,
    });
  }, [navigate, routeLocation.pathname, routeLocation.search, routeLocation.state]);
  const focusId = urlBagRef.current?.focus ?? null;
  const [focusActive, setFocusActive] = useState(Boolean(focusId));
  const [decisionScopeActive, setDecisionScopeActive] = useState(
    urlBagRef.current?.decisionScope === LEASE_DECISION_WINDOW_SCOPE,
  );
  const [decisionLeaseIds, setDecisionLeaseIds] = useState(
    () => urlBagRef.current?.decisionLeaseIds ?? [],
  );
  const decisionLeaseIdSet = useMemo(() => new Set(decisionLeaseIds), [decisionLeaseIds]);
  const decisionScopeExpansionAppliedRef = useRef(false);

  // Band-ordered IDs reported by LeasesTimelineView so the drawer can pass
  // the correct Prev/Next sequence when entering lease detail from Timeline.
  const [timelineOrderedIds, setTimelineOrderedIds] = useState(null);
  const [listSortedIds, setListSortedIds] = useState(null);

  const [leasesAnimKey, setLeasesAnimKey] = useState(0);

  // LEASE-WINDOWS-VIEWALL-FILTER: transient bucket pre-filter from ?buckets= URL
  // param. Not persisted to localStorage — navigating away and back without the
  // param returns to the default unfiltered view.
  const [bucketFilter] = useState(
    () => urlBagRef.current?.buckets ?? null,
  );

  // ── Persisted UI preferences (survive tab close / browser restart) ──────────
  // Shape: { viewMode, density, selections, search, sortKey, sortDir, sortKey2, sortDir2, hideRecurring }
  // clearPrefs is intentionally unused here — clearAll resets only the
  // resetable slice (search/selections/sort) while keeping viewMode+density.
  const [prefs, setPrefs] = useUiPrefs(UI_PREFS_LEASES_V1, {
    viewMode:  "list",
    density:   "comfortable",
    selections: {},
    search:    "",
    showAll:   false,
    hideRecurring: false,
    sortKey:   DEFAULT_LEASES_SORT_STATE.sortKey,
    sortDir:   DEFAULT_LEASES_SORT_STATE.sortDir,
    sortKey2:  DEFAULT_LEASES_SORT_STATE.sortKey2,
    sortDir2:  DEFAULT_LEASES_SORT_STATE.sortDir2,
  }, migrateLeasePrefs);

  const viewMode    = routeLocation.pathname.replace(/\/$/, "") === "/leases/timeline" ||
    (drawerLeaseId && new URLSearchParams(routeLocation.search).get("collection") === "timeline") ? "timeline" : "list";
  const density     = prefs.density     ?? "comfortable";
  const selections  = prefs.selections  ?? {};
  const search      = prefs.search      ?? "";
  const showAll     = prefs.showAll     ?? false;
  // Old arrangement-filter preferences must not become sourced term intent.

  // LEASES-URL-PARAMS: Hydrate all URL params before first paint.
  // URL params win over localStorage so shared links reproduce the intended state.
  // Runs exactly once via useLayoutEffect (sync, no flash of wrong state).
  const urlHydratedRef = useRef(false);
  useLayoutEffect(() => {
    const bag = urlBagRef.current;
    if (urlHydratedRef.current || (!bag && !restoreUrlDefaults)) return;
    urlHydratedRef.current = true;
    setPrefs((s) => {
      const next = restoreUrlDefaults ? {
        // Missing URL density retains the browser preference, as on Offices.
        ...s, search: "", selections: {}, showAll: false,
        sortKey: DEFAULT_LEASES_SORT_STATE.sortKey, sortDir: DEFAULT_LEASES_SORT_STATE.sortDir,
      } : { ...s };
      if (!bag) return next;
      if (bag.q !== undefined) next.search = bag.q;
      if (bag.density) next.density = bag.density;
      // filter dimensions
      const selOverrides = {};
      for (const dim of ["status", "leaseType"]) {
        if (bag[dim] !== undefined) selOverrides[dim] = bag[dim];
      }
      if (Object.keys(selOverrides).length > 0 || bag.checkedFilters) {
        next.selections = { ...(bag.checkedFilters ? {} : next.selections), ...selOverrides };
      }
      if (bag.showAll) next.showAll = true;
      // sort
      if (bag.sort !== undefined) {
        next.sortKey = bag.sort;
        next.sortDir = bag.sortDir ?? (LEASES_NUMERIC_SORT_KEYS.has(bag.sort) ? "desc" : "asc");
      }
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setDensity   = (v) => setPrefs((s) => ({ ...s, density:    typeof v === "function" ? v(s.density)    : v }));
  const setSelections= (v) => setPrefs((s) => ({ ...s, showAll: false, selections: typeof v === "function" ? v(s.selections) : v }));
  const setShowAll   = (v) => setPrefs((s) => ({ ...s, showAll: typeof v === "function" ? v(s.showAll) : v }));
  const setSearch    = (v) => setPrefs((s) => ({ ...s, search:     typeof v === "function" ? v(s.search)     : v }));

  // Sort state — adapts the setSortState(fn) / clearSortState() API that the
  // existing JSX already uses (LEASES-ACTION-VIEW, SortControl, etc.).
  const sortState = {
    sortKey:       prefs.sortKey   ?? DEFAULT_LEASES_SORT_STATE.sortKey,
    sortDir:       prefs.sortDir   ?? DEFAULT_LEASES_SORT_STATE.sortDir,
    sortKey2:      prefs.sortKey2  ?? DEFAULT_LEASES_SORT_STATE.sortKey2,
    sortDir2:      prefs.sortDir2  ?? DEFAULT_LEASES_SORT_STATE.sortDir2,
    userHasSetSort:
      ((prefs.sortKey ?? DEFAULT_LEASES_SORT_STATE.sortKey) !== DEFAULT_LEASES_SORT_STATE.sortKey) ||
      ((prefs.sortDir ?? DEFAULT_LEASES_SORT_STATE.sortDir) !== DEFAULT_LEASES_SORT_STATE.sortDir) ||
      (prefs.sortKey2 != null),
  };
  const setSortState = (updater) => setPrefs((s) => {
    const cur = {
      sortKey:       s.sortKey   ?? DEFAULT_LEASES_SORT_STATE.sortKey,
      sortDir:       s.sortDir   ?? DEFAULT_LEASES_SORT_STATE.sortDir,
      sortKey2:      s.sortKey2  ?? null,
      sortDir2:      s.sortDir2  ?? "asc",
      userHasSetSort: false,
    };
    const next = typeof updater === "function" ? updater(cur) : updater;
    return { ...s, sortKey: next.sortKey, sortDir: next.sortDir, sortKey2: next.sortKey2, sortDir2: next.sortDir2 };
  });
  const clearSortState = () => setPrefs((s) => ({
    ...s,
    sortKey:  DEFAULT_LEASES_SORT_STATE.sortKey,
    sortDir:  DEFAULT_LEASES_SORT_STATE.sortDir,
    sortKey2: DEFAULT_LEASES_SORT_STATE.sortKey2,
    sortDir2: DEFAULT_LEASES_SORT_STATE.sortDir2,
  }));

  // Guard against a stale sortKey persisted from an older field list.
  // If the stored key is no longer in LEASES_SORT_FIELDS, reset to the default.
  useEffect(() => {
    const valid = LEASES_SORT_FIELDS.some((f) => f.value === sortState.sortKey);
    if (!valid) {
      setSortState((prev) => ({ ...prev, ...DEFAULT_LEASES_SORT_STATE }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Display changes replace only their own URL fields; tab links push history.
  // Skip the initial pre-hydration render so saved preferences cannot overwrite
  // an incoming bookmark before its URL fields have been applied.
  const firstRenderDoneRef = useRef(false);
  useEffect(() => {
    if (!firstRenderDoneRef.current && focusId && viewMode !== "timeline") return;
    if (!firstRenderDoneRef.current) {
      firstRenderDoneRef.current = true;
      // Hydration in useLayoutEffect can queue a preference update while this
      // effect still closes over the pre-hydration view. Never replace a deep
      // link with that stale initial view; the next state change will sync it.
      return;
    }
    const encoded = encodeLeasesParams({
      view:           viewMode,
      search,
      selections,
      filterVersion: CHECKED_FILTER_VERSION,
      showAll,
      sortKey:        sortState.sortKey,
      sortDir:        sortState.sortDir,
      userHasSetSort: sortState.userHasSetSort,
      density,
      focusId,
      buckets:        bucketFilter,
      decisionScope: decisionScopeActive ? LEASE_DECISION_WINDOW_SCOPE : null,
      decisionLeaseIds,
    });
    const params = new URLSearchParams(routeLocation.search);
    if (drawerLeaseId && viewMode === "timeline") params.set("collection", "timeline");
    else params.delete("collection");
    for (const key of ["view", "status", "leaseType", "filters", "sort", "sortDir", "q", "density", "peek", "focus", "showAll", "buckets", "decisionScope", "decisionLeaseId"]) params.delete(key);
    for (const [key, value] of new URLSearchParams(encoded)) {
      if (key !== "view") params.append(key, value);
    }
    if (focusId) params.set("focus", focusId);
    const target = routeLocation.pathname + (params.size ? `?${params}` : "") + routeLocation.hash;
    if (target !== routeLocation.pathname + routeLocation.search + routeLocation.hash) replaceDisplayUrl(target);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selections, showAll, sortState.sortKey, sortState.sortDir, sortState.userHasSetSort, density, drawerLeaseId, bucketFilter, focusId, viewMode, decisionScopeActive, decisionLeaseIds, replaceDisplayUrl]);

  const leaseSortKey = sortState.sortKey;
  const leaseSortDir = sortState.sortDir === "desc" ? -1 : 1;

  // Expanded group state (lifted here so toolbar can read/update it).
  const [expandedGroups, setExpandedGroups] = useState(DEFAULT_EXPANDED_GROUPS);
  const [emptyGroupExpansion, setEmptyGroupExpansion] = useState({});

  // Fetch data on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([getPortfolioLeases(), getCriticalDates()])
      .then(([leasesData, cdData]) => {
        if (cancelled) return;
        setLeases(Array.isArray(leasesData) ? leasesData : []);
        setCriticalDates(Array.isArray(cdData) ? cdData : []);
        setLoading(false);
        setLeasesAnimKey((k) => k + 1);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("LeasesPage: fetch failed", { err: String(err) });
        setError(err);
        setLoading(false);
      });

    getUnspentAllowanceRows()
      .then((rows) => { if (!cancelled) setAllowanceRows(Array.isArray(rows) ? rows : []); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [leaseRefreshKey]);

  // onSubtitleChange is retained for LeaseDetailPage (detail-view title);
  // the list subtitle is now static from PAGE_SUBTITLES in AppShell.

  // Index critical dates by leaseId for O(1) lookup.
  const cdByLease = useMemo(() => {
    const idx = {};
    for (const cd of criticalDates) {
      if (!cd.leaseId) continue;
      if (!idx[cd.leaseId]) idx[cd.leaseId] = [];
      idx[cd.leaseId].push(cd);
    }
    return idx;
  }, [criticalDates]);

  // Enrich with action metadata (buckets, days, action type).
  const derivedAll = useMemo(
    () => deriveLeaseActions(leases, criticalDates, TODAY, Infinity),
    [leases, criticalDates],
  );

  // Apply search filter, then the active sort (shared toolbar SortControl +
  // LeasesListTable column headers both write to the same sortState).
  const searched = useMemo(() => applySearch(derivedAll, search), [derivedAll, search]);
  const searchMatches = useMemo(() => search.trim()
    ? new Map(derivedAll.map((lease) => [lease.id, matchLeaseIdentity(lease, search)]))
    : new Map(), [derivedAll, search]);
  // Layer the shared default status filter on top of raw `selections` only
  // when the user hasn't explicitly chosen a status.
  const effectiveSelections = useMemo(() => normalizeCheckedSelections(LEASES_FILTER_DIMENSIONS, {
    ...selections,
    // LEASES-STATUS-FILTER-DESELECT (L-30): preserve an explicit [] (user emptied
    // the group) while still applying the baseline when status is absent
    // (fresh load / after Clear which drops the key entirely).
    //
    // P-33c: When the user arrived via "View all N" from the Home card
    // (?buckets= param active) and has NOT explicitly chosen a status filter,
    // suppress the default status gate so the bucket filter operates on the
    // full searched set — matching the count the Home card computed.
    // User-applied status filters (selections.status !== undefined) still compose.
    status: selections.status !== undefined
      ? selections.status
      : (bucketFilter?.length > 0
          ? allOptionValues(LEASES_FILTER_DIMENSIONS[0])
          : DEFAULT_LEASE_STATUS_STATUSES),
  }), [selections, bucketFilter]);
  // L-42: Remove "draft" from Status options in Timeline view so the filter
  // never shows a checkbox that would always return zero results (Draft leases
  // are unconditionally excluded from Timeline by the L-38 post-filter above).
  const filterDimensions = useMemo(() => {
    if (viewMode !== "timeline") return LEASES_FILTER_DIMENSIONS;
    return LEASES_FILTER_DIMENSIONS.map((dim) => {
      if (dim.id !== "status") return dim;
      return { ...dim, options: dim.options.filter((o) => o.value !== "draft") };
    });
  }, [viewMode]);

  const consolidatedFiltered = useMemo(
    () => decisionScopeActive
      ? derivedAll.filter((lease) => decisionLeaseIdSet.has(lease.id))
      : showAll ? searched : applyConsolidatedFilters(searched, effectiveSelections, viewMode === "list" ? true : "legacy-empty"),
    [decisionScopeActive, decisionLeaseIdSet, derivedAll, showAll, searched, effectiveSelections, viewMode],
  );
  // LEASE-WINDOWS-VIEWALL-FILTER: apply transient bucket pre-filter from
  // ?buckets= URL param. Composes with status/leaseType filters (AND logic).
  const bucketPreFiltered = useMemo(
    () => !decisionScopeActive && bucketFilter?.length > 0
      ? consolidatedFiltered.filter((l) => bucketFilter.includes(l.action_window_bucket))
      : consolidatedFiltered,
    [consolidatedFiltered, bucketFilter, decisionScopeActive],
  );
  // L-38: Draft leases must never appear in the Timeline view, even when
  // "Show all" is active. This unconditional post-filter runs regardless of
  // the showAll short-circuit above so no code path surfaces draft leases
  // in the timeline.
  const timelineFiltered = useMemo(
    () => viewMode === "timeline"
      ? bucketPreFiltered.filter((l) => l.status !== "draft" && l._healthBucket !== "draft")
      : bucketPreFiltered,
    [viewMode, bucketPreFiltered],
  );
  const filtered = useMemo(() => {
    // A focused arrival may be hidden by remembered search/status/lease-type
    // preferences. Include only that record without changing those preferences.
    const target = focusId && viewMode === "timeline"
      ? derivedAll.find((l) => l.id === focusId && l.status !== "draft" && l._healthBucket !== "draft")
      : null;
    const rows = target && !timelineFiltered.some((l) => l.id === focusId)
      ? [...timelineFiltered, target]
      : timelineFiltered;
    const sorted = sortLeaseRows(rows, sortState.sortKey, sortState.sortDir, sortState.sortKey2, sortState.sortDir2);
    return viewMode === "list" ? rankIdentityMatches(sorted, searchMatches) : sorted;
  }, [timelineFiltered, derivedAll, focusId, viewMode, searchMatches, sortState.sortKey, sortState.sortDir, sortState.sortKey2, sortState.sortDir2]);
  const hiddenSearchGroups = useMemo(() => restrictedFilterGroups(
    LEASES_FILTER_DIMENSIONS, effectiveSelections, true,
  ), [effectiveSelections]);
  const countedFilterDimensions = useMemo(() => withFacetCounts(
    filterDimensions, effectiveSelections, decisionScopeActive ? derivedAll : searched,
    (rows, selected, countGroup) => (decisionScopeActive
      ? rows.filter(lease => decisionLeaseIdSet.has(lease.id))
      : showAll ? rows : applyConsolidatedFilters(rows, selected, true, countGroup))
      .filter(lease => decisionScopeActive || !bucketFilter?.length || bucketFilter.includes(lease.action_window_bucket)),
  ), [filterDimensions, effectiveSelections, searched, derivedAll, decisionScopeActive, decisionLeaseIdSet, showAll, bucketFilter]);
  const revealedSelections = useMemo(() => relaxFilterGroups(selections, hiddenSearchGroups, true), [selections, hiddenSearchGroups]);
  const hiddenSearchHint = useMemo(() => {
    if (viewMode !== "list" || decisionScopeActive || showAll || !search.trim() || !hiddenSearchGroups.length) return null;
    // Relax the explained restrictions only; retain unrelated bucket arrival scope.
    const comparisonSelections = relaxFilterGroups(effectiveSelections, hiddenSearchGroups, true);
    const candidates = applyConsolidatedFilters(searched, comparisonSelections, true)
      .filter((lease) => !bucketFilter?.length || bucketFilter.includes(lease.action_window_bucket));
    return deriveFilterHiddenSearch({ query: search, groups: hiddenSearchGroups, selections: effectiveSelections, candidates, visible: filtered, checked: true });
  }, [viewMode, decisionScopeActive, showAll, search, hiddenSearchGroups, effectiveSelections, searched, bucketFilter, filtered]);

  // Obsolete hideRecurring preferences must never narrow Timeline.
  const timelineIncludedLeases = filtered;
  const timelineLeases = useMemo(
    // Home's explicit decision deep-link is an existing scoped arrival, not a
    // request to re-bucket the population by the lease expiry lens.
    // The decision lens stays dormant, regardless of old saved preferences.
    () => decisionScopeActive
      ? timelineIncludedLeases
      : applyTimelineLens(timelineIncludedLeases, cdByLease, "expiry"),
    [decisionScopeActive, timelineIncludedLeases, cdByLease],
  );
  const timelineCoverage = useMemo(
    () => viewMode === "timeline" ? getTimelineDateCoverage(timelineIncludedLeases, cdByLease) : null,
    [viewMode, timelineIncludedLeases, cdByLease],
  );

  useEffect(() => {
    if (!decisionScopeActive) {
      decisionScopeExpansionAppliedRef.current = false;
      return;
    }
    if (loading || viewMode !== "timeline" || decisionScopeExpansionAppliedRef.current) return;
    decisionScopeExpansionAppliedRef.current = true;
    const bucketsToExpand = new Set();
    for (const lease of timelineLeases) {
      if (!decisionLeaseIdSet.has(lease.id)) continue;
      const lane = timelineLaneFor(lease);
      const bucket = TIMELINE_GROUPS.some((group) => group.bucket === lane)
        ? lane
        : "monitor";
      if (expandedGroups[bucket] === false) bucketsToExpand.add(bucket);
    }
    if (bucketsToExpand.size > 0) {
      setExpandedGroups((previous) => {
        const next = { ...previous };
        for (const bucket of bucketsToExpand) next[bucket] = true;
        return next;
      });
    }
  }, [decisionScopeActive, decisionLeaseIdSet, expandedGroups, timelineLeases, loading, viewMode]);

  useEffect(() => {
    if (!focusId || loading || viewMode !== "timeline") return;
    const target = timelineLeases.find((l) => l.id === focusId);
    if (!target) return;
    const lane = timelineLaneFor(target);
    const bucket = TIMELINE_GROUPS.some((g) => g.bucket === lane)
      ? lane : "monitor";
    if (expandedGroups[bucket] === false) {
      setExpandedGroups((prev) => ({ ...prev, [bucket]: true }));
      return;
    }
    const row = Array.from(document.querySelectorAll('[data-testid^="timeline-row-"]'))
      .find((element) => element.getAttribute("data-testid") === `timeline-row-${focusId}`);
    if (!row) return;
    row.scrollIntoView?.({ block: "center", behavior: "smooth" });
    const timer = window.setTimeout(() => setFocusActive(false), 3000);
    return () => window.clearTimeout(timer);
  }, [focusId, loading, viewMode, timelineLeases, expandedGroups]);

  const displayedListIds = useMemo(() => {
    const ids = filtered.map((lease) => lease.id);
    const eligibleIds = new Set(ids);
    return viewMode === "list" && listSortedIds?.length === ids.length
      && listSortedIds.every((id) => eligibleIds.has(id))
      ? listSortedIds : ids;
  }, [filtered, listSortedIds, viewMode]);

  // NAV-PREVNEXT-RESPECT-FILTER-SCOPE: Push current filtered+sorted ID sequence
  // to AppShell so Prev/Next on the location detail page (lease context) respects
  // active filters. Runs whenever `filtered` changes (filter/sort/search change).
  useEffect(() => {
    if (typeof onNavListChange === "function") {
      onNavListChange(displayedListIds);
    }
  }, [displayedListIds, onNavListChange]);

  // The ordered lease list used for drawer arrow nav and Prev/Next pager.
  // In Timeline mode we use the band-ordered list reported by LeasesTimelineView;
  // in List mode (or before the timeline has reported its order) we fall back to
  // the flat filtered array so existing List behaviour is unchanged.
  const drawerOrderedList = useMemo(() => {
    if (viewMode === "timeline" && timelineOrderedIds && timelineOrderedIds.length > 0) {
      const byId = Object.fromEntries(filtered.map((l) => [l.id, l]));
      return timelineOrderedIds.map((id) => byId[id]).filter(Boolean);
    }
    // LIST-KEYBOARD-NAV-ROW-HIGHLIGHT (G-25): in list mode, use the sorted order
    // reported by LeasesListTable (via onSortedIdsChange) so arrow-key nav matches
    // the visual row order. Falls back to the flat filtered array until the table
    // has reported its first sort (first render or before list is visible).
    if (viewMode === "list") {
      const byId = Object.fromEntries(filtered.map((l) => [l.id, l]));
      return displayedListIds.map((id) => byId[id]).filter(Boolean);
    }
    return filtered;
  }, [viewMode, timelineOrderedIds, displayedListIds, filtered]);

  useEffect(() => {
    if (drawerLease == null) return;
    // LIST-KEYBOARD-NAV-ROW-HIGHLIGHT (G-25): use the correct testid per view mode.
    // Timeline rows carry data-testid="timeline-row-*"; list rows carry
    // data-testid="leases-list-row-*". Using the wrong testid silently no-ops.
    const testid = viewMode === "timeline"
      ? `timeline-row-${drawerLease.id}`
      : `leases-list-row-${drawerLease.id}`;
    const row = document.querySelector(`[data-testid="${testid}"]`);
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [drawerLease, viewMode]);

  // KPI tile values.
  // KPI-SCOPE-INDICATOR (G-22): the responsive tiles compute from the filtered
  // list. Avg term and WALE always reflect the full portfolio.
  const portfolioKpis = useMemo(
    () => deriveKpiTiles(derivedAll, allowanceRows),
    [derivedAll, allowanceRows],
  );
  const { avgTermMonths, waleYears, waleIncludedCount, waleTotalCount, waleUndatedCount, waleIncompleteCount, unusedTi, nearExpiryTiCount, subleaseCount, windingDownCount } = portfolioKpis;
  const { leaseCount, totalLeasedSqft, areaEligibleCount, missingAreaCount, excludedOutboundCount, totalRent, description: rentDescription, recoveryDetail, descriptor: rentDescriptor, expiringWithin12moCount } = useMemo(
    () => deriveKpiTiles(filtered, allowanceRows),
    [filtered, allowanceRows],
  );
  // L-61: Unified per-lease alert messages — combines past-expiry (L-37) and
  // missing-rent as first-class alert conditions. One message per distinct lease
  // ID prevents double-counting when both conditions apply to the same lease.
  // Groups: past-expiry-only → missing-rent-only → both, A-Z within each group.
  const leaseDataAlertMessages = useMemo(() => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const todayMs = Date.now();
    const MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30.5;

    const pastExpiryIds  = new Set();
    const missingRentIds = new Set();

    for (const l of leases) {
      // Past-expiry: in-use status AND contractual end_date already passed.
      if (statusFor(l) === "in_use" && l.endDate) {
        const d = new Date(l.endDate);
        if (!isNaN(d.getTime()) && d < todayMidnight) pastExpiryIds.add(l.id);
      }
      // Missing rent: no FX-converted USD rent on an active or holdover lease.
      if (isLeaseRentMissing(l)) missingRentIds.add(l.id);
    }

    const allAffectedIds = new Set([...pastExpiryIds, ...missingRentIds]);
    if (allAffectedIds.size === 0) return [];

    const leaseById = new Map(leases.map((l) => [l.id, l]));
    const pastExpiryOnly  = [];
    const missingRentOnly = [];
    const both            = [];

    for (const id of allAffectedIds) {
      const l   = leaseById.get(id);
      if (!l) continue;
      const isPE = pastExpiryIds.has(id);
      const isMR = missingRentIds.has(id);
      if (isPE && isMR) both.push(l);
      else if (isPE)    pastExpiryOnly.push(l);
      else              missingRentOnly.push(l);
    }

    const labelOf = (l) => (l.locationAlias || l.locationId || l.id).toLowerCase();
    const byLabel = (a, b) => labelOf(a).localeCompare(labelOf(b));
    pastExpiryOnly.sort(byLabel);
    missingRentOnly.sort(byLabel);
    both.sort(byLabel);

    const buildMsg = (l, isPE, isMR) => {
      const label = l.locationAlias || l.locationId || l.id;
      const reasonTag = isPE && isMR ? "Missing rent · Past expiry"
        : isMR ? "Missing rent"
        : "Past expiry";

      let text = `${label} · ${reasonTag}`;
      if (isPE) {
        // Append "Nmo ago" for past-expiry leases — date context is meaningful there.
        const moAgo = Math.floor((todayMs - new Date(l.endDate).getTime()) / MS_PER_MONTH);
        text = `${label} · ${reasonTag} · ${moAgo}mo ago`;
      }

      return {
        id:          `lease-alert-${l.id}`,
        severity:    "warn",
        text,
        actionLabel: "Open →",
        onAction:    () => {
          openRecord(l);
        },
      };
    };

    return [
      ...pastExpiryOnly.map((l) => buildMsg(l, true,  false)),
      ...missingRentOnly.map((l) => buildMsg(l, false, true)),
      ...both.map((l)            => buildMsg(l, true,  true)),
    ];
  }, [leases]); // eslint-disable-line react-hooks/exhaustive-deps

  // Near-expiry TI allowances and other non-per-lease status messages.
  // The stale "decide-now past expiry" summary message was removed (L-61b):
  // per-lease alert rows in leaseDataAlertMessages now cover that condition.
  const leasesStatusMessages = useMemo(() => {
    const msgs = [];
    // Near-expiry TI allowances (≡ isTiNearExpiry from deleted LeasesView):
    // TI allowances with undrawn balance expiring within 90 days.
    if (nearExpiryTiCount > 0) {
      msgs.push({
        id:       "ti-near-expiry",
        severity: "warn",
        text:     `${nearExpiryTiCount} TI allowance${nearExpiryTiCount === 1 ? "" : "s"} expiring within 90 days with undrawn balance.`,
      });
    }
    return msgs;
  }, [nearExpiryTiCount]);

  // L-61b: Derive group counts for the popover summary header.
  // Re-runs whenever `leases` changes — same dep as leaseDataAlertMessages.
  const leaseAlertSummary = useMemo(() => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const pastExpiryIds  = new Set();
    const missingRentIds = new Set();

    for (const l of leases) {
      if (statusFor(l) === "in_use" && l.endDate) {
        const d = new Date(l.endDate);
        if (!isNaN(d.getTime()) && d < todayMidnight) pastExpiryIds.add(l.id);
      }
      if (isLeaseRentMissing(l)) missingRentIds.add(l.id);
    }

    const allAffectedIds  = new Set([...pastExpiryIds, ...missingRentIds]);
    const totalDistinct   = allAffectedIds.size;
    let missingRentOnlyCount = 0;
    let pastExpiryOnlyCount  = 0;
    let bothCount            = 0;
    for (const id of allAffectedIds) {
      const isPE = pastExpiryIds.has(id);
      const isMR = missingRentIds.has(id);
      if (isPE && isMR) bothCount++;
      else if (isPE)    pastExpiryOnlyCount++;
      else              missingRentOnlyCount++;
    }
    return { totalDistinct, missingRentOnlyCount, pastExpiryOnlyCount, bothCount };
  }, [leases]); // eslint-disable-line react-hooks/exhaustive-deps

  // L-61b: Build the dynamic summary header for the alert popover.
  // null when no per-lease alerts are active (no header rendered).
  const leasesAlertPanelHeader = useMemo(() => {
    const { totalDistinct, missingRentOnlyCount, pastExpiryOnlyCount, bothCount } = leaseAlertSummary;
    if (totalDistinct === 0) return null;
    const parts = [];
    if (missingRentOnlyCount > 0) parts.push(`${missingRentOnlyCount} missing rent`);
    if (pastExpiryOnlyCount  > 0) parts.push(`${pastExpiryOnlyCount} past expiry`);
    if (bothCount            > 0) parts.push(`${bothCount} past expiry & missing rent`);
    return `${totalDistinct} lease${totalDistinct === 1 ? "" : "s"} need attention — ${parts.join(", ")}`;
  }, [leaseAlertSummary]);

  // G-19: Single combined alert chip — merges decide-now / TI-near-expiry alerts
  // with per-lease data alerts (past-expiry and/or missing-rent) into one
  // leading toolbar control. Badge count = distinct affected leases (no double-count).
  const combinedLeasesAlertMessages = useMemo(
    () => [...leasesStatusMessages, ...leaseDataAlertMessages],
    [leasesStatusMessages, leaseDataAlertMessages],
  );

  // Column-header sort callback for LeasesListTable — writes into the same
  // sortState as the toolbar SortControl (single source of truth).
  const handleLeaseSort = useCallback((key, dir) => {
    setSortState((s) => ({ ...s, sortKey: key, sortDir: dir === -1 ? "desc" : "asc", userHasSetSort: true }));
  }, [setSortState]);

  // Toolbar SortControl callback.
  const handleSortChange = useCallback(({ primary, secondary }) => {
    setSortState((s) => ({
      ...s,
      sortKey:        primary.key,
      sortDir:        primary.dir,
      sortKey2:       secondary.key,
      sortDir2:       secondary.dir,
      userHasSetSort: true,
    }));
  }, [setSortState]);

  // Toolbar state
  function handleFilterSelectionsChange(newSelections) {
    const incoming = newSelections.status;
    // An absent status preserves bucket-arrival eligibility. An intentional
    // empty selection remains match-none; only the actual baseline is dropped.
    const atBaseline = !incoming || (
      incoming.length === DEFAULT_LEASE_STATUS_STATUSES.length &&
      DEFAULT_LEASE_STATUS_STATUSES.every((status) => incoming.includes(status))
    );
    if (atBaseline) {
      const { status: _drop, ...rest } = newSelections;
      setSelections(rest);
    } else {
      setSelections(newSelections);
    }
  }

  const hasActiveSelections = activeFilterGroups(LEASES_FILTER_DIMENSIONS, effectiveSelections, { status: DEFAULT_LEASE_STATUS_STATUSES }, true).length > 0;
  const hasFilters = search.trim() !== "" || sortState.userHasSetSort === true || hasActiveSelections || showAll || decisionScopeActive;
  const listFacetClearActive = hasActiveSelections || showAll;
  const timelineClearActive = hasActiveSelections || showAll || decisionScopeActive;

  // KPI-SCOPE-INDICATOR (G-22): true when a filter or search is actively
  // narrowing the list from the full portfolio.
  const isFiltered = search.trim() !== "" || hasActiveSelections;

  // List toolbar owns facets only; empty-state and Timeline keep their
  // independent full-reset contracts, including contextual scope.
  const clearListFacets = useCallback(() => {
    setPrefs((s) => ({
      ...s,
      selections: normalizeCheckedSelections(LEASES_FILTER_DIMENSIONS, {}, {}, { preserveMissing: ["status"] }),
      showAll: false,
    }));
  }, [setPrefs]);

  const clearAll = useCallback(() => {
    // Reset only the clearable slice (search + selections + sort + showAll) while
    // preserving viewMode and density so tab layout doesn't jump.
    setPrefs((s) => ({
      ...s,
      search:    viewMode === "timeline" ? s.search : "",
      selections: normalizeCheckedSelections(LEASES_FILTER_DIMENSIONS, {}, {}, { preserveMissing: ["status"] }),
      showAll:   false,
      sortKey:   DEFAULT_LEASES_SORT_STATE.sortKey,
      sortDir:   DEFAULT_LEASES_SORT_STATE.sortDir,
      sortKey2:  DEFAULT_LEASES_SORT_STATE.sortKey2,
      sortDir2:  DEFAULT_LEASES_SORT_STATE.sortDir2,
    }));
    setDecisionScopeActive(false);
  }, [setPrefs, viewMode]);

  // Empty bands start collapsed even when populated versions of those bands
  // normally start expanded. Keep explicit empty-band toggles separate so a
  // filter change does not alter the populated-band defaults.
  const filteredBucketCounts = useMemo(() => {
    const counts = Object.fromEntries(TIMELINE_GROUPS.map((group) => [group.bucket, 0]));
    for (const lease of timelineLeases) {
      const bucket = timelineLaneFor(lease);
      const targetBucket = counts[bucket] === undefined ? "monitor" : bucket;
      counts[targetBucket] += 1;
    }
    return counts;
  }, [timelineLeases]);
  const timelineExpandedGroups = useMemo(() => Object.fromEntries(
    TIMELINE_GROUPS.map((group) => [
      group.bucket,
      filteredBucketCounts[group.bucket] === 0
        ? (emptyGroupExpansion[group.bucket] ?? false)
        : (expandedGroups[group.bucket] ?? group.expandedByDefault),
    ]),
  ), [emptyGroupExpansion, expandedGroups, filteredBucketCounts]);

  const handleGroupToggle = useCallback((bucket) => {
    if (filteredBucketCounts[bucket] === 0) {
      setEmptyGroupExpansion((prev) => ({ ...prev, [bucket]: !Boolean(prev[bucket]) }));
      return;
    }
    setExpandedGroups((prev) => ({ ...prev, [bucket]: !prev[bucket] }));
  }, [filteredBucketCounts]);

  const listDrawerUrl = useCallback((id) => {
    const params = new URLSearchParams(routeLocation.search);
    if (viewMode === "timeline") params.set("collection", "timeline");
    return `${buildLeaseUrl(id, "overview", params)}${routeLocation.hash}`;
  }, [routeLocation.search, routeLocation.hash, viewMode]);
  const handleTimelineLeaseSelect = useCallback((lease) => {
    if (!lease?.id) return;
    const returnTo = `${routeLocation.pathname}${routeLocation.search}${routeLocation.hash}`;
    const orderedIds = viewMode === "timeline" && timelineOrderedIds?.length
      ? timelineOrderedIds
      : displayedListIds;
    navigate(listDrawerUrl(lease.id), {
      state: { ...routeLocation.state, orderedIds, returnTo },
    });
  }, [displayedListIds, navigate, timelineOrderedIds, viewMode, listDrawerUrl, routeLocation.pathname, routeLocation.search, routeLocation.hash, routeLocation.state]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div data-testid="leases-v2-page" style={{ minHeight: "100%" }}>

      <PageHeaderStack>
      {/* ── Error banner ──────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          background:   "var(--v2-red-tint)",
          border:       "1px solid var(--v2-red)",
          borderRadius: 10,
          padding:      "10px 14px",
          fontSize:     T.fsSm,
          color:        "var(--v2-red)",
        }}>
          Failed to load lease data - {error.message || String(error)}
        </div>
      )}

      {/* ── KPI tiles ─────────────────────────────────────────────────────── */}
      {/* KPI-SCOPE-INDICATOR (G-22): tile order is responsive-cluster (left)
          then portfolio tiles (right). Responsive tiles react to active filters;
          Avg term and WALE remain portfolio-wide. */}
      <KpiBand testId="kpi-tiles-row">
        <KpiTile
          testId="kpi-lease-count"
          height={KPI_TILE_ROW_HEIGHT}
          label="Leases"
          tooltip={<KpiPopover period="Today" source="Spacebase">
            All active and committed leases in the portfolio.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="all active and committed leases"
          value={loading ? "…" : String(leaseCount)}
          rawValue={loading ? undefined : leaseCount}
          animationKey={leasesAnimKey}
          tileIndex={0}
          color={KPI_ACCENTS[0]}
          scope="responsive"
          isFiltered={isFiltered}
        />
        <KpiTile
          testId="kpi-total-rent"
          height={KPI_TILE_ROW_HEIGHT}
          label="Annual base rent"
          tooltip={<KpiPopover period="Today" source="Spacebase">
            {rentDescription} <strong>FX basis:</strong> {fxBasisSummary}
            {recoveryDetail && <div>{recoveryDetail}</div>}
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor={rentDescriptor}
          rawValue={loading ? undefined : totalRent}
          formatter={fmtKpiUsd}
          animationKey={leasesAnimKey}
          tileIndex={1}
          color={KPI_ACCENTS[1]}
          scope="responsive"
          isFiltered={isFiltered}
        />
        <KpiTile
          testId="kpi-leased-sqft"
          height={KPI_TILE_ROW_HEIGHT}
          label="SQ FT"
          tooltip={<KpiPopover period="Today" source="Spacebase" details={[
            { label: "Recorded usable area", value: `${areaEligibleCount - missingAreaCount}/${areaEligibleCount} area-eligible leases` },
            { label: "Missing usable area", value: `${missingAreaCount}/${areaEligibleCount} area-eligible leases` },
            { label: "Outbound excluded", value: `${excludedOutboundCount} leases` },
          ]}>
            Recorded leased area across In-Use, Vacant-Held, and Committed leases in the current view.
            Available current-area footage is preferred; otherwise valid unit-normalized stored lease size is used.
            Head-lease area is counted in full; outbound subleases are excluded to prevent double counting, even if their head is hidden.
            Inbound subleases remain eligible. Missing or invalid area is excluded, not replaced with Offices area.
            This is leased active + committed area, not the Offices operational footprint.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="across active + committed leases."
          value={loading ? "…" : undefined}
          rawValue={loading ? undefined : totalLeasedSqft}
          formatter={(n) => n >= 1_000_000
            ? (n / 1_000_000).toFixed(1) + "M"
            : Math.round(n).toLocaleString("en-US")}
          animationKey={leasesAnimKey}
          tileIndex={2}
          color={KPI_ACCENTS[2]}
          scope="responsive"
          isFiltered={isFiltered}
        />
        <KpiTile
          testId="kpi-expiring-12mo"
          height={KPI_TILE_ROW_HEIGHT}
          label="Expiring &lt; 12 mo"
          tooltip={<KpiPopover period="Today" source="Spacebase (derived)">
            Active leases ending within 12 months.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="leases ending within 12 months"
          value={loading ? "…" : String(expiringWithin12moCount)}
          rawValue={loading ? undefined : expiringWithin12moCount}
          animationKey={leasesAnimKey}
          tileIndex={3}
          color={KPI_ACCENTS[5]}
          scope="responsive"
          isFiltered={isFiltered}
        />
        {/* Portfolio tile: always reflects the full derivedAll list regardless
            of active filters — it is a portfolio-wide average, not a sum/count. */}
        <KpiTile
          testId="kpi-avg-term"
          height={KPI_TILE_ROW_HEIGHT}
          label="Avg term left"
          tooltip={<KpiPopover period="Today" source="Spacebase (derived)">
            Simple average of remaining term across active leases - every lease counts equally.
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="across active leases"
          value={loading ? "…" : undefined}
          rawValue={loading ? undefined : avgTermMonths}
          formatter={fmtAvgTerm}
          animationKey={leasesAnimKey}
          tileIndex={4}
          color={KPI_ACCENTS[2]}
          scope="portfolio"
        />
        <KpiTile
          testId="kpi-wale"
          height={KPI_TILE_ROW_HEIGHT}
          label="WALE"
          tooltip={<KpiPopover
            period="Today"
            source="Spacebase (derived)"
            details={[{
              label: "Included in calculation",
              value: loading ? "—" : String(waleIncludedCount),
            }, {
              label: "Excluded for incomplete data",
              value: loading ? "—" : String(waleIncompleteCount),
            }, {
              label: "Undated (excluded)",
              value: loading ? "—" : String(waleUndatedCount),
            }]}
          >
            Remaining term weighted by dollar exposure - large, expensive leases dominate. Higher than the simple average means your spend is committed longer than your lease count suggests. Based on {loading ? "—" : waleIncludedCount} of {loading ? "—" : waleTotalCount} active leases; {loading ? "—" : waleIncompleteCount} excluded for incomplete data, {loading ? "—" : waleUndatedCount} undated.
            {!loading && waleYears == null ? " No usable lease exposure supports a calculation." : ""}
          </KpiPopover>}
          tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
          tooltipPlacement="viewport-clamped"
          descriptor="weighted by exposure"
          value={loading ? "…" : waleYears == null ? "—" : undefined}
          rawValue={loading ? undefined : waleYears}
          formatter={fmtWale}
          animationKey={leasesAnimKey}
          tileIndex={6}
          color={KPI_ACCENTS[3]}
          scope="portfolio"
        />
      </KpiBand>
      <>{/* ── Toolbar ───────────────────────────────────────────────────────── */}
      {/* Search · Filter · Clear · spacer · List|Timeline · + Add lease */}
      <InlineFilterToolbar
        collectionActions
        countBelow reuseCountGap
        afterCount={<div data-testid="leases-search-hint-anchor" style={{ marginTop: hiddenSearchHint ? 4 : 0 }}>
          <FilterHiddenSearchHint hint={hiddenSearchHint} entity="lease" empty={filtered.length === 0}
            onClear={() => setSelections(revealedSelections)} />
        </div>}
        testId="leases-toolbar" leftTestId="leases-toolbar-left"
        countTestId="leases-showing-count"
        count={viewMode === "timeline" ? timelineLeases.length : filtered.length} total={leases.length}
        trailing={<ViewControls
          object="leases"
          view={viewMode}
          densityNodes={{
            // Reuse the existing per-view density seam without changing the shared manifest.
            [viewMode === "timeline" ? "expandCollapse" : "compactComfortable"]: () => (
              <CompactSwitch
                checked={density === "compact"}
                onChange={() => setDensity((d) => d === "comfortable" ? "compact" : "comfortable")}
                testId="leases-compact-switch"
              />
            ),
          }}
          viewToggle={viewMode === "timeline" ? <TimelineViewKey coverage={timelineCoverage} /> : null}
          addButton={canWrite ? <ToolbarAddButton variant="primary" testId="add-lease-btn" onClick={() => setShowCreateLease(true)}>
            Add lease
          </ToolbarAddButton> : null}
        />}
      >
        {/* Left zone — Search → Filter → Sort in List mode (+ Clear all). */}

          {/* Alert chip — leads the toolbar row; actionable only on the list
              view, so hidden entirely (no reserved space) on timeline where
              urgency is already surfaced visually by the groups.
              G-19: single combined chip merging decide-now, TI-near-expiry,
              and past-due in-use alerts into one aggregate control. */}
          {viewMode === "list" && (
            <AlertChip
              testId="leases-alert-chip"
              storageKey="ripple.leases.alertChip.v2.open"
              messages={combinedLeasesAlertMessages}
              okMessage={{ text: "No lease alerts" }}
              panelHeader={leasesAlertPanelHeader}
            />
          )}

          {/* Search */}
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search leases…"
            testId="v2-lease-search"
          />

          {/* Filter — shared ConsolidatedFilter (LEASES-FILTER-BUTTON-FIX-S1).
              Pass effectiveSelections so the panel shows the shared baseline
              pre-checked; Clear restores the same baseline in either view. */}
          <ConsolidatedFilter
            checkedInclusion
            unifiedDefaults={viewMode === "list"}
            dimensions={viewMode === "list" ? countedFilterDimensions : filterDimensions}
            selections={effectiveSelections}
            baselineSelections={{ status: DEFAULT_LEASE_STATUS_STATUSES }}
            showAll={showAll}
            onShowAllChange={setShowAll}
            hideBadge={showAll}
            onChange={handleFilterSelectionsChange}
            testId="v2-filter-btn"
          />

          <InlineFilterChips prefix="leases" chips={[
            ...(viewMode === "list" && !decisionScopeActive && !showAll ? facetChips({
              dimensions: filterDimensions, selections: effectiveSelections,
              baseline: { status: DEFAULT_LEASE_STATUS_STATUSES }, checked: true, onChange: handleFilterSelectionsChange,
            }) : []),
            ...(decisionScopeActive ? [{
              id: "decision-scope", testId: "decision-scope-filter", removeTestId: "clear-decision-scope",
              role: "group", ariaLabel: "Active lease scope",
              label: `Scope: Decisions due — next ${LEASE_DECISION_WINDOW_MONTHS} months`,
              removeLabel: `Clear Decisions due — next ${LEASE_DECISION_WINDOW_MONTHS} months filter`,
              onRemove: () => setDecisionScopeActive(false),
            }] : []),
          ]} />

          {/* Sort remains available in List only; Timeline orders within its bands. */}
          {viewMode === "list" && (
            <SortControl
              testId="v2-sort"
              fields={LEASES_SORT_FIELDS}
              defaultKey={DEFAULT_LEASES_SORT_STATE.sortKey}
              primary={{ key: sortState.sortKey, dir: sortState.sortDir }}
              secondary={{ key: sortState.sortKey2, dir: sortState.sortDir2 }}
              onChange={handleSortChange}
            />
          )}

          {/* Timeline ignores query-only and ineffective List sort activity. */}
          {(viewMode === "timeline" ? timelineClearActive : listFacetClearActive) && (
            <InlineFilterClear testId="clear-filters-btn" onClick={viewMode === "timeline" ? clearAll : clearListFacets} />
          )}
      </InlineFilterToolbar>

      {/* ── Timeline view ─────────────────────────────────────────────────── */}
      {viewMode === "timeline" && (
        <>
          {!loading && timelineLeases.length === 0 && (
            <div data-testid="empty-state" style={{
              textAlign:  "center",
              padding:    "48px 0",
              color:      "var(--v2-light)",
              fontSize:   T.fsSm,
            }}>
              {search.trim() || timelineClearActive ? "No leases match the current search or filters." : "No active leases found."}
            </div>
          )}
          {!loading && (
            <LeasesTimelineView
              leases={timelineLeases}
              cdByLease={cdByLease}
              onSelectLocation={onSelectLocation}
              expandedGroups={timelineExpandedGroups}
              onGroupToggle={handleGroupToggle}
              onSelectLease={handleTimelineLeaseSelect}
              selectedLeaseId={drawerLease?.id ?? null}
              focusLeaseId={focusActive ? focusId : null}
              onOrderedIdsChange={setTimelineOrderedIds}
            />
          )}
        </>
      )}

      {/* ── List view ─────────────────────────────────────────────────────── */}
      {viewMode === "list" && (
        <div data-testid="leases-v2-list-view">
          <LeasesListTable
            presentation="v2"
            presorted
            leases={filtered}
            searchQuery={search}
            loading={loading}
            totalLeaseCount={leases.length}
            onSelectLocation={onSelectLocation}
            onRowClick={handleTimelineLeaseSelect}
            getDrawerUrl={listDrawerUrl}
            onClearFilters={!hiddenSearchHint && hasFilters ? clearAll : undefined}
            cdByLease={cdByLease}
            leaseSortKey={leaseSortKey}
            leaseSortDir={leaseSortDir}
            leaseSortKey2={sortState.sortKey2}
            leaseSortDir2={sortState.sortDir2 === "desc" ? -1 : 1}
            onLeaseSort={handleLeaseSort}
            density={density}
            userHasSetSort={sortState.userHasSetSort}
            orderedIds={displayedListIds}
            selectedLeaseId={drawerLease?.id ?? null}
            onSortedIdsChange={setListSortedIds}
          />
        </div>
      )}
      </>

      </PageHeaderStack>

      {/* ── Peek drawer ───────────────────────────────────────────────────── */}
      <LeaseRecordDrawer
        leaseId={drawerLeaseId}
        tab={drawerTab}
        currentRole={currentRole}
        currentUser={currentUser}
        orderedIds={drawerOrderedList.map((lease) => lease.id)}
        onTabChange={(tab) => openRecord(drawerLease, { tab, replace: true })}
        onNavigate={(id) => openRecord({ id }, { tab: drawerTab, replace: true })}
        onClose={() => {
          const params = new URLSearchParams(routeLocation.search);
          params.delete("tab"); params.delete("peek"); params.delete("collection");
          navigate(`${viewMode === "timeline" ? "/leases/timeline" : "/leases"}${params.size ? `?${params}` : ""}`, { state: routeLocation.state });
        }}
        onOpenFull={(id, tab) => navigate(buildLeaseFullUrl(id, tab, routeLocation.search), {
          state: { ...routeLocation.state, orderedIds: drawerOrderedList.map((lease) => lease.id), returnTo: routeLocation.pathname + routeLocation.search },
        })}
        onOpenOffice={(id, lease) => navigate(buildOfficeDrawerUrl(id), {
          state: recordSwapState({ kind: "lease", id: drawerLeaseId, label: lease?.name || drawerLeaseId,
            path: routeLocation.pathname + routeLocation.search }),
        })}
        recordReturn={readRecordReturn(routeLocation.state)}
        onFollowReturn={(path, state) => navigate(path, { state })}
        onLeaseSaved={() => setLeaseRefreshKey((key) => key + 1)}
      />

      {/* ── Create lease modal (L-47) ─────────────────────────────────────── */}
      {canWrite && <CreateLeaseModal
        isOpen={showCreateLease}
        onClose={() => setShowCreateLease(false)}
        onSaved={() => {
          setShowCreateLease(false);
          setLeaseRefreshKey((k) => k + 1);
        }}
      />}
    </div>
  );
}
